import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Grants from "./pages/Grants";
import GrantDetail from "./pages/GrantDetail";
import Applications from "./pages/Applications";
import Bookmarks from "./pages/Bookmarks";
import Profile from "./pages/Profile";
import Pricing from "./pages/Pricing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import PostGrantManager from "./pages/PostGrantManager";
import PostGrantDetail from "./pages/PostGrantDetail";
import VendorDirectory from "./pages/VendorDirectory";
import CorporateScout from "./pages/CorporateScout";
import CapitalAccess from "./pages/CapitalAccess";
import FarmerDigest from "./pages/FarmerDigest";
import Documents from "./pages/Documents";
import ImpactDashboard from "./pages/ImpactDashboard";
import Collaborate from "./pages/Collaborate";
import Coalitions from "./pages/Coalitions";
import CoalitionPlaybook from "./pages/CoalitionPlaybook";
import TrialSuccess from "./pages/TrialSuccess";
import GrantCalendar from "./pages/GrantCalendar";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminGrants from "./pages/AdminGrants";
import AdminApplications from "./pages/AdminApplications";
import CorporateLeads from "./pages/CorporateLeads";
import ForCorporations from "./pages/ForCorporations";
import ResetPassword from "./pages/ResetPassword";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminImpactEmails from "./pages/AdminImpactEmails";
import AdminFollowUps from "./pages/AdminFollowUps";
import AdminFunderStatus from "./pages/AdminFunderStatus";
import AdminVerificationQueue from "./pages/AdminVerificationQueue";
import AdminNewsletter from "./pages/AdminNewsletter";
import AdminRetainerLinks from "./pages/AdminRetainerLinks";
import AdminClientSprints from "./pages/AdminClientSprints";
import ClientSetupSprint from "./pages/ClientSetupSprint";
import Feedback from "./pages/Feedback";
import MissionDiagnostic from "./pages/MissionDiagnostic";
import Formation1023EZ from "./pages/Formation1023EZ";
import BylawsTemplates from "./pages/BylawsTemplates";
import FormationToolkit from "./pages/FormationToolkit";
import Sponsorships from "./pages/Sponsorships";
import SponsorPublic from "./pages/SponsorPublic";
import GivingLog from "./pages/GivingLog";
import LocalHero from "./pages/LocalHero";
import LocalHeroMicrosite from "./pages/LocalHeroMicrosite";
import FoundingMember from "./pages/FoundingMember";
import FounderSlides from "./pages/FounderSlides";
import PlaybookArkOfRefuge from "./pages/PlaybookArkOfRefuge";
import PlaybookSeniorCareNetwork from "./pages/PlaybookSeniorCareNetwork";
import PlaybookDoc from "./pages/PlaybookDoc";
import Pathways from "./pages/Pathways";
import StartANonprofitInSC from "./pages/StartANonprofitInSC";
import NotFound from "./pages/NotFound";
import FcsIntake from "./pages/FcsIntake";
import AdminFcsIntakes from "./pages/AdminFcsIntakes";
import AdminDocuments from "./pages/AdminDocuments";
import TrialResumer from "./components/TrialResumer";
import AnalyticsTracker from "./components/AnalyticsTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor - send session token from localStorage via Authorization header
// NOTE: Explicitly set withCredentials=false to prevent browser from sending
// Cloudflare cookies (__cf_bm), which triggers credentials mode and causes
// CORS rejection when Cloudflare injects Access-Control-Allow-Origin: *
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = false;
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("session_token");
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  // useLocation() subscribes to route changes so AppRouter re-renders after navigate() calls.
  // We still check window.location.hash for the actual value (more reliable on mobile).
  useLocation();
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <TrialResumer />
    <AnalyticsTracker />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/for-corporations" element={<ForCorporations />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/grants" element={<Grants />} />
      <Route path="/capital" element={<CapitalAccess />} />
      <Route path="/farmer-digest" element={<FarmerDigest />} />
      <Route path="/grants/:grantId" element={<GrantDetail />} />
      <Route path="/applications" element={
        <ProtectedRoute><Applications /></ProtectedRoute>
      } />
      <Route path="/bookmarks" element={
        <ProtectedRoute><Bookmarks /></ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute><GrantCalendar /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><Profile /></ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>
      } />
      <Route path="/post-grants" element={
        <ProtectedRoute><PostGrantManager /></ProtectedRoute>
      } />
      <Route path="/post-grants/:postGrantId" element={
        <ProtectedRoute><PostGrantDetail /></ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute><VendorDirectory /></ProtectedRoute>
      } />
      <Route path="/corporate-scout" element={
        <ProtectedRoute><CorporateScout /></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><Documents /></ProtectedRoute>
      } />
      <Route path="/impact" element={
        <ProtectedRoute><ImpactDashboard /></ProtectedRoute>
      } />
      <Route path="/collaborate" element={
        <ProtectedRoute><Collaborate /></ProtectedRoute>
      } />
      <Route path="/coalitions" element={
        <ProtectedRoute><Coalitions /></ProtectedRoute>
      } />
      <Route path="/coalitions/playbook" element={<CoalitionPlaybook />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/formation" element={
        <ProtectedRoute><FormationToolkit /></ProtectedRoute>
      } />
      <Route path="/formation/mission-diagnostic" element={
        <ProtectedRoute><MissionDiagnostic /></ProtectedRoute>
      } />
      <Route path="/formation/1023-ez" element={
        <ProtectedRoute><Formation1023EZ /></ProtectedRoute>
      } />
      <Route path="/formation/bylaws-templates" element={
        <ProtectedRoute><BylawsTemplates /></ProtectedRoute>
      } />
      <Route path="/coalitions/:coalitionId" element={
        <ProtectedRoute><Coalitions /></ProtectedRoute>
      } />
      <Route path="/trial/success" element={
        <ProtectedRoute><TrialSuccess /></ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>
      } />
      <Route path="/admin/grants" element={
        <ProtectedRoute requireAdmin><AdminGrants /></ProtectedRoute>
      } />
      <Route path="/admin/applications" element={
        <ProtectedRoute requireAdmin><AdminApplications /></ProtectedRoute>
      } />
      <Route path="/admin/corporate-leads" element={
        <ProtectedRoute requireAdmin><CorporateLeads /></ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute requireAdmin><AdminAnnouncements /></ProtectedRoute>
      } />
      <Route path="/admin/impact-emails" element={
        <ProtectedRoute requireAdmin><AdminImpactEmails /></ProtectedRoute>
      } />
      <Route path="/admin/follow-ups" element={
        <ProtectedRoute requireAdmin><AdminFollowUps /></ProtectedRoute>
      } />
      <Route path="/admin/funder-status" element={
        <ProtectedRoute requireAdmin><AdminFunderStatus /></ProtectedRoute>
      } />
      <Route path="/admin/verification-queue" element={
        <ProtectedRoute requireAdmin><AdminVerificationQueue /></ProtectedRoute>
      } />
      <Route path="/admin/newsletter" element={
        <ProtectedRoute requireAdmin><AdminNewsletter /></ProtectedRoute>
      } />
      <Route path="/admin/retainer-links" element={
        <ProtectedRoute requireAdmin><AdminRetainerLinks /></ProtectedRoute>
      } />
      <Route path="/admin/client-sprints" element={
        <ProtectedRoute requireAdmin><AdminClientSprints /></ProtectedRoute>
      } />
      <Route path="/admin/client-sprints/:slug" element={
        <ProtectedRoute requireAdmin><ClientSetupSprint /></ProtectedRoute>
      } />
      <Route path="/sponsorships" element={
        <ProtectedRoute><Sponsorships /></ProtectedRoute>
      } />
      <Route path="/sponsor/:id" element={<SponsorPublic />} />
      <Route path="/local-hero" element={<LocalHero />} />
      <Route path="/local-hero/:slug" element={<LocalHeroMicrosite />} />
      <Route path="/founding" element={<FoundingMember />} />
      <Route path="/start-a-nonprofit-in-sc" element={<StartANonprofitInSC />} />
      <Route path="/slides" element={<FounderSlides />} />
      <Route path="/slides/:n" element={<FounderSlides />} />
      <Route path="/slides/:deck/:n" element={<FounderSlides />} />
      <Route path="/playbook/ark-of-refuge" element={<PlaybookArkOfRefuge />} />
      <Route path="/playbook/senior-care-network" element={<PlaybookSeniorCareNetwork />} />
      <Route path="/playbook/doc/:slug" element={<PlaybookDoc />} />
      <Route path="/pathways" element={<Pathways />} />
      <Route path="/fcs/intake" element={<FcsIntake />} />
      <Route path="/admin/documents" element={
        <ProtectedRoute requireAdmin><AdminDocuments /></ProtectedRoute>
      } />
      <Route path="/admin/fcs-intakes" element={
        <ProtectedRoute requireAdmin><AdminFcsIntakes /></ProtectedRoute>
      } />
      <Route path="/giving-log" element={
        <ProtectedRoute><GivingLog /></ProtectedRoute>
      } />
      
      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
